import {
    each, isArray, isBoolean, isObject, sumBy,
} from 'lodash'
import widgetFilterType from '@/constants/widgetFilterType'

export function useWidgetFilterHelpers() {
    const filterValueValid = (filter, value) => {
        if (!isObject(value)) {
            return false
        }

        switch (filter.type) {
            case widgetFilterType.enabled:
                return isBoolean(value.enabled)
            case widgetFilterType.flatSelect:
            case widgetFilterType.select:
                return isArray(value.selected)
            default:
                return false
        }
    }

    const filterValue = (filter, selectedFilters) => {
        return selectedFilters[filter.dependency_id]?.[filter.id]
    }

    const filterActiveCount = (filter, selectedFilters) => {
        const value = filterValue(filter, selectedFilters)
        if (!filterValueValid(filter, value)) {
            return 0
        }

        switch (filter.type) {
            case widgetFilterType.enabled:
                return value.enabled ? 1 : 0
            case widgetFilterType.flatSelect:
            case widgetFilterType.select:
                return value.selected.length
            default:
                return 0
        }
    }

    const isActive = (filter, selectedFilters) => {
        return filterActiveCount(filter, selectedFilters) > 0
    }

    const activeCount = (filterGroups, selectedFilters) => {
        return sumBy(filterGroups, (group) => {
            return sumBy(group.filters, (filter) => {
                return filterActiveCount(filter, selectedFilters)
            })
        })
    }

    const defaultFilterValue = (filter) => {
        switch (filter.type) {
            case widgetFilterType.enabled:
                return { enabled: false }
            case widgetFilterType.flatSelect:
            case widgetFilterType.select:
                return { selected: [] }
            default:
                return {}
        }
    }

    const applyFilterToDependency = (filter, selectedFilters, dependency) => {
        // If a filter has removed the dependency, there's no need to apply further filters
        if (!dependency) {
            return dependency
        }

        // If the filter doesn't apply to the dependency, return it as is
        if (dependency.id !== filter.dependency_id) {
            return dependency
        }

        const value = filterValue(filter, selectedFilters)
            ?? defaultFilterValue(filter)

        // If there's no valid value for the filter, treat it as inactive and return the dependency as is
        if (!filterValueValid(filter, value)) {
            return dependency
        }

        switch (filter.type) {
            case widgetFilterType.enabled:
                return value.enabled ? dependency : null
            case widgetFilterType.flatSelect:
            case widgetFilterType.select: {
                if (value.selected.length === 0) {
                    return null
                }
                const url = new URL(dependency.url)
                each(value.selected, (selectedValue) => {
                    url.searchParams.append(`${filter.id}[]`, selectedValue)
                })
                return {
                    ...dependency,
                    url: url.toString(),
                }
            }
            default:
                return dependency
        }
    }

    const applyFiltersToDependency = (filterGroups, selectedFilters, dependency) => {
        // If no filters are active, return the dependency as is
        if (activeCount(filterGroups, selectedFilters) === 0) {
            return dependency
        }

        let result = dependency
        each(filterGroups, (group) => {
            each(group.filters, (filter) => {
                result = applyFilterToDependency(filter, selectedFilters, result)
            })
        })
        return result
    }

    return {
        filterActiveCount,
        filterValue,
        isActive,
        activeCount,
        applyFiltersToDependency,
    }
}
